import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Box,
  Chip,
  Pagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  PlayCircle as PlayCircleIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';

const Search = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    // 模拟搜索请求
    try {
      // 这里应该是API调用
      const mockResults = [
        {
          id: '1',
          title: '热门剧集1',
          description: '这是一个热门剧集的描述',
          poster: 'https://via.placeholder.com/300x450',
          category: '悬疑',
          views: 1200,
          comments: 89,
        },
        {
          id: '2',
          title: '最新上线剧集',
          description: '这是一个最新上线剧集的描述',
          poster: 'https://via.placeholder.com/300x450',
          category: '爱情',
          views: 850,
          comments: 45,
        },
        {
          id: '3',
          title: '高分推荐',
          description: '这是一个高分推荐剧集的描述',
          poster: 'https://via.placeholder.com/300x450',
          category: '喜剧',
          views: 1500,
          comments: 120,
        },
      ];

      // 模拟网络延迟
      setTimeout(() => {
        setSearchResults(mockResults);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('搜索失败:', error);
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: isMobile ? 1 : 2 }}>
      {/* 搜索框 */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
          <TextField
            fullWidth
            label="搜索剧集"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            sx={{ minWidth: isMobile ? '100%' : 120, fontSize: isMobile ? '0.875rem' : '1rem' }}
          >
            搜索
          </Button>
        </Box>
      </Paper>

      {/* 搜索结果 */}
      {searchResults.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant={isMobile ? 'h6' : 'h5'} 
            component="h2" 
            sx={{ mb: 3, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
          >
            搜索结果 ({searchResults.length} 个结果)
          </Typography>
          <Grid container spacing={2}>
            {searchResults.map((drama) => (
              <Grid item xs={12} sm={6} md={4} key={drama.id}>
                <Paper
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={drama.poster}
                      alt={drama.title}
                      style={{
                        width: '100%',
                        aspectRatio: '4/3',
                        objectFit: 'cover',
                        cursor: 'pointer',
                      }}
                      onClick={() => console.log('Play drama:', drama.id)}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Chip
                        icon={<PlayCircleIcon />}
                        label={`${drama.views}次播放`}
                        size="small"
                        color="primary"
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '0.7rem',
                        }}
                      />
                      <Chip
                        icon={<FavoriteIcon />}
                        label="收藏"
                        size="small"
                        color="secondary"
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ p: isMobile ? 1.5 : 2 }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography 
                        variant={isMobile ? 'subtitle1' : 'h6'} 
                        component="h3" 
                        sx={{ mb: 1, fontSize: isMobile ? '1rem' : '1.25rem' }}
                      >
                        {drama.title}
                      </Typography>
                      <Chip
                        label={drama.category}
                        size="small"
                        color="primary"
                        sx={{ mr: 1, mb: 1, fontSize: '0.75rem' }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, flexGrow: 1, fontSize: isMobile ? '0.875rem' : '0.9rem' }}
                    >
                      {drama.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* 搜索历史 */}
      <Paper sx={{ p: isMobile ? 2 : 4, borderRadius: 2 }}>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="h2" 
          sx={{ mb: 3, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
        >
          搜索历史
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['悬疑剧集', '爱情故事', '喜剧片', '科幻电影'].map((term) => (
            <Chip
              key={term}
              label={term}
              size={isMobile ? 'small' : 'medium'}
              color="primary"
              variant="outlined"
              sx={{ 
                cursor: 'pointer',
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}
              onClick={() => {
                setSearchQuery(term);
                handleSearch();
              }}
            />
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

export default Search;
